import {
  useAtsCandidateDiscoveryApi,
  useAtsCandidateDiscoveryApiWithOptions,
} from '@/composables/useApi';
import { shouldRetryTransientFailure } from '@/composables/useApi.util';
import type { Employer, UserProfile } from '@factoryfixinc/ats-interfaces';
import type { GatedUserProfile } from './types/gated-user-profile.type';
import { InternalError } from '@/core/shared/errors/internal.error';

export interface CandidateResumeSignedUrl {
  signedUrl: string;
  expiresAt: string;
}
const useCandidateResumeSignerApi = useAtsCandidateDiscoveryApiWithOptions({
  retry: 1,
  retryIf: shouldRetryTransientFailure,
});

export default class SelectedCandidatePersistence {
  async fetchCandidate(
    employerId: Employer['id'],
    proUserProfileId: UserProfile['id'],
  ): Promise<GatedUserProfile> {
    const url = `search-talent/employer/${employerId}/candidates/${proUserProfileId}`;
    const { data, error, statusCode } = await useAtsCandidateDiscoveryApi(url)
      .get()
      .json<GatedUserProfile>();

    if (error.value) {
      throw new InternalError('Failed to fetch candidate', {
        error: error.value,
        status: statusCode.value,
        data: { employerId, proUserProfileId },
      });
    }

    if (!data.value) {
      throw new InternalError('Empty response received from fetchCandidate', {
        status: statusCode.value,
        data: { employerId, proUserProfileId },
      });
    }

    return data.value;
  }

  async getResumeSignedUrl(
    employerId: Employer['id'],
    proUserProfileId: UserProfile['id'],
  ): Promise<CandidateResumeSignedUrl> {
    const url = `search-talent/employer/${employerId}/candidates/${proUserProfileId}/resume/signed-url`;
    const { data, error, statusCode } = await useCandidateResumeSignerApi(url)
      .post()
      .json<CandidateResumeSignedUrl>();

    if (error.value || !data.value?.signedUrl || !data.value.expiresAt) {
      throw new InternalError('Failed to sign candidate resume', {
        error: error.value,
        status: statusCode.value,
        data: { employerId, proUserProfileId },
      });
    }

    return data.value;
  }
}
